import { ReactNode } from 'react';

import { Checkbox } from '@tg-web/components';
import { PropsWithCN, cn } from '@tg-web/utils';

export type IslandCheckboxCellProps = PropsWithCN & {
  checked: boolean;
  children: ReactNode;
  /** Mirrors the control: the row stops responding and stops looking tappable. */
  disabled?: boolean;
  id: string;
  onCheckedChange: (checked: boolean) => void;
};

/**
 * A 56px row with a checkbox in the leading slot, where the whole row toggles.
 *
 * The row is the `<label>` itself, not a div wrapping one. That is what makes
 * every part of it — the gutters and the icon column included — a tap target,
 * and it is also what keeps the toggle single: a div with its own onClick would
 * fire once for the Radix checkbox (a real button) and once again as the click
 * bubbled, landing back where it started. Label activation does not re-forward
 * a click that originated inside the labelled control, so one tap is one
 * toggle wherever it lands.
 *
 * `cursor-pointer` is doing more than it looks: iOS only applies `:active` to
 * elements it considers clickable, and that is how the other island cells get
 * their highlight on a plain div too.
 */
export function IslandCheckboxCell({
  checked,
  children,
  className,
  disabled = false,
  id,
  onCheckedChange,
}: IslandCheckboxCellProps) {
  return (
    <label
      className={cn(
        'flex h-14 w-full items-center px-4 transition-colors',
        disabled
          ? 'cursor-not-allowed'
          : 'cursor-pointer active:bg-black/[0.06]',
        className,
      )}
      htmlFor={id}
    >
      <div className="mr-[18px] flex h-[26px] w-[26px] shrink-0 items-center justify-center">
        <Checkbox
          checked={checked}
          disabled={disabled}
          id={id}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
      </div>
      <span className="typo-text text-tg-text min-w-0 flex-1 truncate">
        {children}
      </span>
    </label>
  );
}
